/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2021 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import { BOOTSTRAP_PLUGIN, REDUCER } from 'airglow';
import { match } from 'ramda';
import sagaPlugin from '@airglow/plugin-saga';
import devtoolPlugin from '@airglow/plugin-devtools';
import prefabPlugin from '@airglow/plugin-prefab';
import consolePlugin from '@airglow/plugin-console';

import aldrinPlugin from '@nebula/corona-aldrin-plugin';
import floodgatePlugin from '@nebula/corona-floodgate-plugin';
import izzyPlugin from '@nebula/corona-izzy-plugin';
import roverPlugin from '@nebula/corona-rover-plugin';
import spectrumPlugin from '@nebula/corona-spectrum-plugin';
import testPlugin from '@nebula/corona-test-plugin';
import toastPlugin from '@nebula/corona-toast-plugin';
import Toaster from '@nebula/luna/dist/core/toaster';

import unifiedShellPlugin from '@nebula/airglow-plugin-unified-shell';
import aldrinConfig from './aldrin.config';
import doradoReduxStorage, { reducer } from './dorado.redux.storage';
import routes from './routes';
import * as localeData from './locales';

const locales = {
  'en-US': 'locales/en-US.json',
  'en-UPPER': '/__VERSION__/locales/en-UPPER.json',
  'de-DE': '/__VERSION__/locales/de-DE.json',
  'es-ES': '/__VERSION__/locales/es-ES.json',
  'fr-FR': '/__VERSION__/locales/fr-FR.json',
  'it-IT': '/__VERSION__/locales/it-IT.json',
  'ja-JP': '/__VERSION__/locales/ja-JP.json',
  'ko-KR': '/__VERSION__/locales/ko-KR.json',
  'pt-BR': '/__VERSION__/locales/pt-BR.json',
  'zh-CN': '/__VERSION__/locales/zh-CN.json',
  'zh-TW': '/__VERSION__/locales/zh-TW.json'
};

export const DEFAULT_VIEW_PLUGINS = [
  'event-list',
  'validation-editor',
  'validation-summary',
  'analytics',
  'places-event-list',
  'places-map'
];

const shellPlugin = (
  runtime,
  {
    orgViewPlugins = DEFAULT_VIEW_PLUGINS,
    orgValidationPlugins = {},
    orgWorkspaces = [],
    sessionSettings = {},
    userViewPlugins = [],
    userValidationPlugins = {},
    userWorkspaces = []
  } = {}
) => unifiedShellPlugin({
  runtime,
  localeData,
  feedback: {
    buttonLabel: 'Feedback',
    enabled: true,
    type: 'external',
    url: 'https://app.slack.com/client/THCDA3F89/CHN7VDGMD'
  },
  help: [
    {
      href: 'https://placesdocs.com/',
      description: 'Location Service Documentation',
      icon: 'AdobeExperienceCloud'
    },
    {
      href: 'https://forums.adobe.com/community/experience-cloud/platform/location-service/overview',
      description: 'Location Service Community',
      icon: 'AdobeExperienceCloud'
    },
    {
      href: 'https://launch.gitbook.io/places-services-by-adobe-documentation/release-notes',
      description: 'Location Service: What\'s New',
      icon: 'AdobeExperienceCloud'
    }
  ],
  settings: [
    {
      groupId: 'griffon',
      settings: {
        recentSessions: []
      },
      level: 'user'
    },
    {
      groupId: 'griffon',
      settings: {
        enabledViewPlugins: userViewPlugins,
        validationPlugins: userValidationPlugins,
        workspaces: userWorkspaces,
        workspace: {}
      },
      level: 'userorg'
    },
    {
      groupId: 'griffon',
      settings: {
        enabledViewPlugins: orgViewPlugins,
        validationPlugins: orgValidationPlugins,
        sessionSettings,
        workspaces: orgWorkspaces,
        workspace: {}
      },
      level: 'org'
    },
    {
      groupId: 'griffon',
      settings: { recentLinks: [] }
    }
  ]
});

const doradoReduxStorePlugin = () => (engine) => {
  engine.plugin(REDUCER, reducer);
  engine.plugin(BOOTSTRAP_PLUGIN, () => {
    doradoReduxStorage.setStore(engine.getStore());
  });
};

export const cypressPlugins = () => (window.inCypress ? [
  (engine) => {
    engine.plugin(BOOTSTRAP_PLUGIN, () => {
      window.REDUX_STORE = engine.getStore();
    });
  }
] : []);

const locationMatches = match(/(\/solutions\/dms-mobile-gemini)?(.*)/, window.location.pathname);
const location = locationMatches.length === 3 ? locationMatches[2] : window.location.pathname;

export default (runtime, shellOptions) => [
  aldrinPlugin(aldrinConfig),
  sagaPlugin(),
  shellPlugin(runtime, shellOptions),
  doradoReduxStorePlugin(),
  roverPlugin({
    location,
    matcherOptions: { segmentValueCharset: 'a-zA-Z0-9-_~ %/.' },
    routes
  }),
  devtoolPlugin({ reduxBrowser: true }),
  prefabPlugin(),
  izzyPlugin({ locales }),
  spectrumPlugin(),
  floodgatePlugin({ autoload: false }),
  consolePlugin({}),
  toastPlugin({ component: Toaster }),
  ...cypressPlugins(runtime)
];

export const testPlugins = (runtime, shellOptions) => [
  doradoReduxStorePlugin(),
  aldrinPlugin(aldrinConfig),
  sagaPlugin(),
  devtoolPlugin({ reduxBrowser: true }),
  prefabPlugin(),
  izzyPlugin({ locales }),
  spectrumPlugin(),
  floodgatePlugin({ autoload: false }),
  testPlugin(),
  consolePlugin({}),
  shellPlugin(runtime, shellOptions),
  toastPlugin({ component: Toaster })
];
